import React, { useEffect, useState } from 'react';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { listContacts, updateContact } from '../../../services/SubmissionService';
import './Contact.css';

const getStatusClass = (status) => String(status || 'unknown').toLowerCase().replace(/\s+/g, '-');
const getContactId = (contact) => contact.uuid || contact.id;
const displayValue = (value) => value === null || value === undefined || value === '' ? '—' : String(value);
const loadErrorMessage = (error) => error instanceof TypeError
    ? 'Unable to connect to the contacts service.'
    : error.message || 'Unable to load contact submissions.';
const statuses = ['Pending', 'Read', 'Replied', 'Archived'];

const Contact = () => {
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedContact, setSelectedContact] = useState(null);
    const [statusValue, setStatusValue] = useState('Pending');
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [notice, setNotice] = useState('');

    const loadContacts = async () => {
        setError('');
        setIsLoading(true);
        try {
            const result = await listContacts();
            setContacts(result.items);
        } catch (loadError) {
            setError(loadErrorMessage(loadError));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadContacts(); }, []);

    const openContact = (contact) => {
        setSelectedContact(contact);
        setStatusValue(contact.status || 'Pending');
        setStatusError('');
        setNotice('');
    };

    const saveStatus = async () => {
        if (!selectedContact || !getContactId(selectedContact) || !statusValue) return;
        setIsSavingStatus(true);
        setStatusError('');
        try {
            const updatedContact = await updateContact(getContactId(selectedContact), { status: statusValue });
            setContacts(current => current.map(item => getContactId(item) === getContactId(selectedContact) ? { ...item, ...updatedContact, status: updatedContact?.status || statusValue } : item));
            setSelectedContact(current => ({ ...current, ...updatedContact, status: updatedContact?.status || statusValue }));
            setNotice(`Contact status updated to ${updatedContact?.status || statusValue}.`);
        } catch (saveError) {
            setStatusError(loadErrorMessage(saveError));
        } finally {
            setIsSavingStatus(false);
        }
    };

    return (
        <div className="contact">
            <NavigationMenu />
            <div className="contact-content">
                <header className="contact-header"><h1 className="contact-title">Contact</h1><p className="contact-subtitle">Review messages submitted through the website</p></header>
                <main className="contact-main">
                    {notice && <div className="contact-notice" role="status">{notice}</div>}
                    {isLoading ? <div className="submission-feedback">Loading contact submissions...</div> : error ? (
                        <div className="submission-feedback submission-error" role="alert"><p>{error}</p><button type="button" className="feedback-button" onClick={loadContacts}>Try again</button></div>
                    ) : contacts.length === 0 ? (
                        <div className="submission-empty"><h2>No contact submissions</h2><p>New messages will appear here when they are submitted.</p></div>
                    ) : (
                        <div className="table-container"><div className="table-header"><div><h2 className="table-title">Contact submissions</h2><p className="table-caption">{contacts.length} submission{contacts.length === 1 ? '' : 's'}</p></div></div><div className="table-scroll"><table className="data-table"><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Message</th><th>Status</th><th>Actions</th></tr></thead><tbody>
                            {contacts.map(contact => <tr key={getContactId(contact)}><td>{displayValue(contact.name)}</td><td>{displayValue(contact.email)}</td><td>{displayValue(contact.phone)}</td><td className="message-cell">{displayValue(contact.message)}</td><td><span className={`status-badge ${getStatusClass(contact.status)}`}>{displayValue(contact.status)}</span></td><td><button type="button" className="action-btn view" onClick={() => openContact(contact)}>Review</button></td></tr>)}
                        </tbody></table></div></div>
                    )}
                </main>
            </div>

            {selectedContact && <div className="contact-modal-overlay" role="presentation"><div className="contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-detail-title"><div className="modal-header"><div><span className="modal-kicker">Submission {getContactId(selectedContact)}</span><h3 className="modal-title" id="contact-detail-title">{displayValue(selectedContact.name)}</h3></div><button type="button" className="modal-close" onClick={() => setSelectedContact(null)} aria-label="Close contact submission">×</button></div><div className="modal-body"><div className="detail-grid"><div><span>Email</span><strong><a href={`mailto:${selectedContact.email}`}>{displayValue(selectedContact.email)}</a></strong></div><div><span>Phone</span><strong><a href={`tel:${selectedContact.phone}`}>{displayValue(selectedContact.phone)}</a></strong></div><div><span>Submitted</span><strong>{displayValue(selectedContact.createdAt)}</strong></div></div><div className="detail-block"><span>Message</span><p>{displayValue(selectedContact.message)}</p></div><div className="status-editor"><label htmlFor="contact-status">Contact status</label><select id="contact-status" className="modal-input" value={statusValue} onChange={event => setStatusValue(event.target.value)}>{statuses.map(status => <option key={status} value={status}>{status}</option>)}</select>{statusError && <p className="field-error" role="alert">{statusError}</p>}</div></div><div className="modal-footer"><button type="button" className="modal-button cancel" onClick={() => setSelectedContact(null)}>Close</button><button type="button" className="modal-button create" onClick={saveStatus} disabled={isSavingStatus}>{isSavingStatus ? 'Saving...' : 'Save Status'}</button></div></div></div>}
        </div>
    );
};

export default Contact;